import type { Block, Inline } from '@st/util/markdown'
import { match } from '@st/pattern'

export function GuideContent({ blocks }: { blocks: Block[] }) {
  return blocks.map((block, key) =>
    match(block)
      .with({ type: 'heading' }, ({ level, content }) => {
        const text = <InlineContent tokens={content} />
        return match(level)
          .with(1, () => (
            <h1 key={key} className="text-3xl font-semibold">
              {text}
            </h1>
          ))
          .with(2, () => (
            <h2 key={key} className="pt-4 text-xl font-semibold">
              {text}
            </h2>
          ))
          .otherwise(() => (
            <h3 key={key} className="pt-2 text-lg font-semibold">
              {text}
            </h3>
          ))
      })
      .with({ type: 'paragraph' }, ({ content }) => (
        <p key={key}>
          <InlineContent tokens={content} />
        </p>
      ))
      .with({ type: 'list' }, ({ ordered, items }) => {
        const children = items.map((blocks, index) => (
          <li key={index} className="space-y-2">
            <GuideContent blocks={blocks} />
          </li>
        ))
        if (ordered)
          return (
            <ol key={key} className="list-decimal space-y-3 pl-6">
              {children}
            </ol>
          )
        return (
          <ul key={key} className="list-disc space-y-2 pl-6">
            {children}
          </ul>
        )
      })
      .with({ type: 'table' }, ({ rows }) => (
        <div key={key} className="overflow-x-auto">
          <table className="w-full border-collapse text-left">
            <tbody>
              {rows.map((row, index) => (
                <tr key={index}>
                  {row.map((content, column) => {
                    if (index == 0)
                      return (
                        <th key={column} className="border p-2">
                          <InlineContent tokens={content} />
                        </th>
                      )
                    return (
                      <td key={column} className="border p-2 align-top">
                        <InlineContent tokens={content} />
                      </td>
                    )
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))
      .exhaustive()
  )
}

function InlineContent({ tokens }: { tokens: Inline[] }) {
  return tokens.map((token, key) =>
    match(token)
      .with({ type: 'text' }, ({ text }) => text)
      .with({ type: 'strong' }, ({ text }) => <strong key={key}>{text}</strong>)
      .with({ type: 'break' }, () => <br key={key} />)
      .with({ type: 'link' }, ({ text, href }) => (
        <a
          key={key}
          href={href}
          className="text-indigo-600 underline"
          target="_blank"
          rel="noopener noreferrer"
        >
          {text}
        </a>
      ))
      .exhaustive()
  )
}
